<template>
  <VCard class="v-form-builder-sections" size="small" :title="i18n.sections">
    <div
      v-for="(section, index) in blueprint.visibleSections"
      :key="section.id"
      class="v-form-builder-sections__section"
      :class="{ 'is-active': isActive(section) }"
      :draggable="editable ? 'true' : undefined"
      @click="blueprint.activeSection = section"
      @dragend="draggable.dragend"
      @dragover="draggable.dragover(index, $event)"
      @dragstart="draggable.dragstart(index, $event)"
    >
      <div class="flex-center">
        <template v-if="editable && isActive(section) && blueprint.visibleSections.length > 1">
          <VIcon draggable="true" icon="Grab" :size="15" style="margin-right: 0" type="placeholder" />
          <VButton
            icon="Delete"
            :icon-size="15"
            icon-type="placeholder"
            rounded
            size="small"
            style="margin-right: 5px"
            type="icon"
            @click.stop="confirmRemove(section)"
            @dragstart.prevent.stop
          />
        </template>
        <span>
          {{ section.title }}
          <VTransitionCollapse class="v-form-builder-sections__errors" :visible="section.id in errors">
            {{ t(i18n.countQuestionsRequireAttention, errors[section.id]) }}
          </VTransitionCollapse>
        </span>
      </div>
      <VProgress
        class="v-form-builder-sections__progress"
        :height="3"
        :type="section.id in errors ? 'red' : progress[section.id] === 100 ? 'green' : 'blue-5'"
        :value="progress[section.id]"
        width="50px"
      />
    </div>
    <VButton v-if="editable" icon="Plus" :label="i18n.addSection" style="margin-top: 20px" @click="blueprint.addSection" />
  </VCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useDraggable } from '../../composables/useDraggable.ts'
import { t } from '../../utils/helpers.ts'
import { i18nFormBuilder as i18n } from '../../utils/i18n.ts'
import { $confirm } from '../Confirm/index.ts'
import VIcon from '../Icon/VIcon.vue'
import VButton from '../VButton.vue'
import VCard from '../VCard.vue'
import VProgress from '../VProgress.vue'
import VTransitionCollapse from '../VTransitionCollapse.vue'
import { type Blueprint, type CustomQuestionDefinition, type Question, type Section } from './useBlueprint.ts'

interface Props {
  blueprint: Blueprint
  editable?: boolean
}

const props = defineProps<Props>()

const draggable = useDraggable(props.blueprint.visibleSections)

const errors = computed((): Record<number, number> => {
  const errors: Record<number, number> = {}

  for (const key in props.blueprint.errors) {
    const questionId = Number(key)
    const question = props.blueprint.questions.find((question) => question.id === questionId)

    if (question) {
      errors[question.section_id] ??= 0
      errors[question.section_id]++
    }
  }

  return errors
})

const progress = computed((): Record<number, number> => {
  const progress: Record<number, number> = {}

  for (const section of props.blueprint.visibleSections) {
    const sectionProgress = {
      total: 0,
      current: 0,
    }

    for (const question of props.blueprint.questions) {
      if (question.section_id !== section.id || question.optional || (question.internal && !props.blueprint.settings.isInternal)) {
        continue
      }

      const definition = props.blueprint.customQuestionDefinitions[question.type] as CustomQuestionDefinition
      if (definition?.calculateQuestionProgress) {
        definition.calculateQuestionProgress(question, sectionProgress)
        continue
      }

      calculateQuestionProgress(question, sectionProgress)
    }

    if (sectionProgress.total) {
      progress[section.id] = (sectionProgress.current / sectionProgress.total) * 100
    } else {
      progress[section.id] = 100
    }
  }

  return progress
})

function calculateQuestionProgress(question: Question, progress: { total: number; current: number }) {
  // Optional questions don't count towards progress.
  if (question.optional || (question.internal && !props.blueprint.settings.isInternal)) {
    return
  }
  // Questions under `true_false` that are not visible should not count.
  if (props.blueprint.isQuestionHiddenByUnselectedTrueFalseOption(question)) {
    return
  }

  if (question.type === 'repeater') {
    return
  }

  if (question.is_template) {
    return
  }

  // `upload` questions: Only parent questions are counted towards progress (and if one of the child uploads has an answer).
  let parent: undefined | Question = undefined
  if (question.parent_question_id) {
    parent = props.blueprint.questionsById[question.parent_question_id]
    if (!parent) {
      console.warn('parent question not found for question', question)
    }
  }

  if (question.type === 'upload') {
    if (parent?.type === 'upload') {
      return
    }

    progress.total++
    for (const blueprintQuestion of props.blueprint.questions) {
      if (blueprintQuestion.type === 'upload' && blueprintQuestion.parent_question_id === question.id && blueprintQuestion.answer) {
        progress.current++
        break
      }
    }

    return
  }

  progress.total++
  if (Array.isArray(question.answer)) {
    if (question.answer.length) {
      progress.current++
    }
  } else if (question.answer || question.answer === 0 || question.answer === false) {
    progress.current++
  }
}

function isActive(section: Section) {
  return section.id === props.blueprint.activeSection?.id
}

async function confirmRemove(section: Section) {
  try {
    await $confirm({ message: i18n.confirmRemove.replace('$title', section.title) })
    props.blueprint.removeSection(section.id)
  } catch {
    //
  }
}
</script>

<style>
.v-form-builder-sections .v-card__header {
  padding-bottom: 10px;
}
.v-form-builder-sections .v-card__body {
  padding-top: 0;
}

.v-form-builder-sections__section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 5px;
  padding: 3px 10px;
  min-height: 40px;
  margin-bottom: 5px;
  cursor: pointer;
  transition:
    background-color var(--transition),
    transform var(--transition);

  &:hover {
    background-color: var(--color-blue-1);
    transform: translateX(5px);
  }

  &.is-active {
    background-color: var(--color-page-background);
  }
}

.v-form-builder-sections__errors {
  color: var(--color-red);
  font-size: 12px;
}

.v-form-builder-sections__progress {
  background-color: var(--color-grey-10);
  width: 50px;
  height: 3px;
  border-radius: 3px;
}
</style>
